// Utils
import { getData } from '../../attachments/attachmentsSelector';
import { prop, propIn } from '../../../../common/utils/immutableHelper';
import { getHeight, getWidth } from '../../../../common/maths/geometry/rect';
import {
    getImageOriginalProp,
    getImageProp,
    getMediaHeight,
    getMediaWidth,
} from '../../../../common/elements/utils/elementPropertyUtils';

// Types
import type {
    ImMNElement,
    MNElement,
    MNImageContent,
    MNImageEditorDataContent,
} from '../../../../common/elements/elementModelTypes';

type ImageModalElement = MNElement | ImMNElement;
type ImageModalImage = MNImageContent | undefined | null;

type ImageSize = 'original' | 'large' | 'regular';

type ImageSizeOptions = {
    element: ImageModalElement;
    gridSize: number;
    attachment?: unknown;
};

type ImagePropReader<T> = (element: ImageModalElement) => T | undefined;

export const getOriginalImage = (element: ImageModalElement) => getImageOriginalProp(element) || getImageProp(element);

export const getImageEditorData: ImagePropReader<MNImageEditorDataContent> = propIn(['content', 'imageEditorData']);
export const getImageEditorDataRotation: ImagePropReader<number> = propIn(['content', 'imageEditorData', 'rotate']);
export const getImageEditorDataScaleX: ImagePropReader<number> = propIn(['content', 'imageEditorData', 'scaleX']);
export const getImageEditorDataScaleY: ImagePropReader<number> = propIn(['content', 'imageEditorData', 'scaleY']);

// An unset editor scale means "no flip", so default to 1.
export const getImageEditorScale = (scale?: number) => scale || 1;
export const getImageEditorDataScaleXOrDefault = (element: ImageModalElement) =>
    getImageEditorScale(getImageEditorDataScaleX(element));
export const getImageEditorDataScaleYOrDefault = (element: ImageModalElement) =>
    getImageEditorScale(getImageEditorDataScaleY(element));

// Cropped Images
export const getImageWidth: (image: ImageModalImage) => number = prop('width');
export const getImageHeight: (image: ImageModalImage) => number = prop('height');

export const getCroppedImageWidth = ({ element, gridSize, attachment }: ImageSizeOptions) =>
    getMediaWidth(element)
        ? getMediaWidth(element) * gridSize
        : getImageWidth(getData(attachment) || getImageProp(element));

export const getCroppedImageHeight = ({ element, gridSize, attachment }: ImageSizeOptions) =>
    getMediaHeight(element)
        ? getMediaHeight(element) * gridSize
        : getImageHeight(getData(attachment) || getImageProp(element));

export const getOriginalImageWidth = ({ element, gridSize, attachment }: ImageSizeOptions) =>
    getWidth(getOriginalImage(element)) || getCroppedImageWidth({ element, gridSize, attachment });
export const getOriginalImageHeight = ({ element, gridSize, attachment }: ImageSizeOptions) =>
    getHeight(getOriginalImage(element)) || getCroppedImageHeight({ element, gridSize, attachment });

const getOriginalSizeUrl = prop('original');
const getLargeSizeUrl = prop('large');
const getRegularSizeUrl = prop('regular');

export const getLargestImageSize = (image: ImageModalImage): ImageSize | undefined =>
    (getOriginalSizeUrl(image) && 'original') ||
    (getLargeSizeUrl(image) && 'large') ||
    (getRegularSizeUrl(image) && 'regular');

export const getLargestImageUrl = (image: ImageModalImage): string | undefined =>
    getOriginalSizeUrl(image) || getLargeSizeUrl(image) || getRegularSizeUrl(image);

/**
 * Checks if a boolean-like image modal route parameter is enabled.
 */
export const isRouteParamEnabled = (value?: string | boolean): boolean => value === true || value === 'true';
